/**
 * Build the API payload from form state.
 * Centralizes the payload construction logic for job create/update.
 */
import { buildTechSplits } from "../../../utils/job-form-utils";

export function buildJobPayload(formData, { customerMode, parts, payments, teamMembers, paymentTypes, jobTypes, sendAssignmentSMS }) {
  const buildSplitFields = (fd, ids) => {
    const { mode, unit, splits } = buildTechSplits(
      fd.assigned_to, ids, fd.tech_commission_split_mode, fd.tech_commission_splits_map,
      fd.tech_commission_split_unit || "percent", fd.tech_commission_splits_amount_map
    );
    return {
      tech_commission_split_mode: mode, tech_commission_split_unit: unit, tech_commission_splits: splits,
      tech_commission_splits_map: undefined, tech_commission_splits_amount_map: undefined,
    };
  };
  const isCompleted = formData.status === "completed" || formData.status === "completed_pending_payment";
  let jobTitle = formData.title.trim();
  if (!jobTitle) {
    const service = jobTypes.find(jt => jt.service_id === formData.service_id);
    if (formData.vehicle_year && formData.vehicle_make && formData.vehicle_model) {
      jobTitle = `${service?.name || "Service"} - ${formData.vehicle_year} ${formData.vehicle_make} ${formData.vehicle_model}`;
    } else {
      jobTitle = service?.name || "Service Job";
    }
  }

  const additionalIds = (formData.additional_technician_ids || []).filter(t => t && t.trim());

  return {
    ...formData,
    title: jobTitle,
    customer_id: customerMode === "new" ? "" : formData.customer_id,
    customer_name: customerMode === "new" ? formData.customer_name : undefined,
    customer_phone: customerMode === "new" ? formData.customer_phone : undefined,
    customer_phone2: formData.customer_phone2?.trim() || null,
    customer_email: customerMode === "new" ? formData.customer_email : undefined,
    customer_address: formData.customer_address,
    scheduled_date: `${formData.scheduled_date.getFullYear()}-${String(formData.scheduled_date.getMonth() + 1).padStart(2, '0')}-${String(formData.scheduled_date.getDate()).padStart(2, '0')}`,
    schedule_type: formData.schedule_type === "between" && formData.scheduled_time_end ? "between" : "exact",
    scheduled_time_end: formData.schedule_type === "between" ? (formData.scheduled_time_end || null) : null,
    latitude: formData.latitude ?? null,
    longitude: formData.longitude ?? null,
    duration_hours: parseFloat(formData.duration_hours) || 1,
    total_charged: isCompleted ? parseFloat(formData.total_charged) || 0 : null,
    source_commission_type: isCompleted ? formData.source_commission_type : null,
    source_commission_value: isCompleted ? parseFloat(formData.source_commission_value) || 0 : null,
    source_parts_deduction: formData.source_parts_deduction || "from_total",
    technician_commission_type: isCompleted ? formData.technician_commission_type : null,
    technician_commission_value: isCompleted ? parseFloat(formData.technician_commission_value) || 0 : null,
    parts: isCompleted && parts.length > 0 ? parts : null,
    payments: isCompleted && payments.length > 0 ? payments : null,
    vehicle_make: formData.vehicle_make || null,
    vehicle_model: formData.vehicle_model || null,
    vehicle_year: formData.vehicle_year || null,
    send_assignment_sms: sendAssignmentSMS,
    tech_parts_deduction: formData.assigned_to ? (teamMembers.find(m => m.member_id === formData.assigned_to)?.parts_deduction || "none") : "none",
    charge_processing_fees: formData.assigned_to ? (teamMembers.find(m => m.member_id === formData.assigned_to)?.charge_processing_fees !== false) : true,
    // Multi-technician assignment — the backend folds the array, dedupes,
    // and computes the commission split (equal or custom percentages).
    additional_technician_ids: additionalIds.length > 0 ? additionalIds : null,
    ...buildSplitFields(formData, additionalIds),
    additional_technician_id: null,
    additional_tech_commission_type: null,
    additional_tech_commission_value: null,
    // Editable dates: null-out blanks so backend keeps existing values
    created_at: formData.created_at || null,
    completed_at: formData.completed_at || null,
  };
}
